// Content CMS API surface — subjects → tracks → units → lessons → activities.
// Maps to apps/api/src/routes/admin/content.ts. Staff tool, English chrome.

import { api } from './api';

// ─── Tree ─────────────────────────────────────────────────────────────────

export type ContentStatus = 'draft' | 'in_review' | 'published';

export interface TreeLesson {
  id: string;
  titleKey: string;
  summaryKey: string | null;
  estMinutes: number;
  sortOrder: number;
  status: ContentStatus;
}
export interface TreeUnit {
  id: string;
  titleKey: string;
  descriptionKey: string | null;
  sortOrder: number;
  isFree: boolean;
  status: ContentStatus;
  lessons: TreeLesson[];
}
export interface TreeTrack {
  id: string;
  grade: string;
  variant: string;
  language: string;
  titleKey: string;
  units: TreeUnit[];
}
export interface TreeSubject {
  id: string;
  slug: string;
  nameKey: string;
  icon: string | null;
  sortOrder: number;
  tracks: TreeTrack[];
}

export async function getTree(signal?: AbortSignal): Promise<TreeSubject[]> {
  const { subjects } = await api<{ subjects: TreeSubject[] }>('/admin/content/tree', { signal });
  return subjects;
}

// ─── Lesson detail (authoring view — payloads INCLUDE answer keys) ─────────

export interface ActivityRow {
  id: string;
  type: string;
  sortOrder: number;
  payload: unknown;
  scoringRules: { passScore: number; xp: number; graded: boolean };
  updatedAt: string;
}
export interface LessonDetail {
  id: string;
  unitId: string;
  titleKey: string;
  summaryKey: string | null;
  coverMediaId: string | null;
  estMinutes: number;
  status: ContentStatus;
  updatedAt: string;
}

export async function getLesson(
  id: string,
  signal?: AbortSignal,
): Promise<{ lesson: LessonDetail; activities: ActivityRow[] }> {
  return api(`/admin/content/lessons/${id}`, { signal });
}

// ─── Subjects ───────────────────────────────────────────────────────────────

export async function createSubject(input: {
  slug: string;
  nameKey: string;
  icon?: string | null;
}): Promise<{ id: string }> {
  return api('/admin/content/subjects', { method: 'POST', body: input });
}
export async function updateSubject(
  id: string,
  input: Partial<{ nameKey: string; icon: string | null; sortOrder: number }>,
): Promise<void> {
  await api(`/admin/content/subjects/${id}`, { method: 'PATCH', body: input });
}
export async function deleteSubject(id: string): Promise<void> {
  await api(`/admin/content/subjects/${id}`, { method: 'DELETE' });
}

// ─── Tracks ─────────────────────────────────────────────────────────────────

export const GRADES = ['kg2', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6'] as const;
export const TRACK_VARIANTS = ['standard', 'native', 'non_native_speaker'] as const;
export const LANGUAGES = ['ar', 'en'] as const;

export async function createTrack(input: {
  subjectId: string;
  grade: string;
  variant?: string;
  language?: string;
  titleKey: string;
}): Promise<{ id: string }> {
  return api('/admin/content/tracks', { method: 'POST', body: input });
}
export async function updateTrack(id: string, input: { titleKey?: string }): Promise<void> {
  await api(`/admin/content/tracks/${id}`, { method: 'PATCH', body: input });
}
export async function deleteTrack(id: string): Promise<void> {
  await api(`/admin/content/tracks/${id}`, { method: 'DELETE' });
}

// ─── Units ────────────────────────────────────────────────────────────────

export async function createUnit(input: {
  trackId: string;
  titleKey: string;
  descriptionKey?: string | null;
}): Promise<{ id: string }> {
  return api('/admin/content/units', { method: 'POST', body: input });
}
export async function updateUnit(
  id: string,
  input: Partial<{
    titleKey: string;
    descriptionKey: string | null;
    sortOrder: number;
    isFree: boolean;
  }>,
): Promise<void> {
  await api(`/admin/content/units/${id}`, { method: 'PATCH', body: input });
}
export async function deleteUnit(id: string): Promise<void> {
  await api(`/admin/content/units/${id}`, { method: 'DELETE' });
}
export async function reorderUnits(parentId: string, ids: string[]): Promise<void> {
  await api('/admin/content/units/reorder', { method: 'POST', body: { parentId, ids } });
}

// ─── Lessons ──────────────────────────────────────────────────────────────

export async function createLesson(input: {
  unitId: string;
  titleKey: string;
  summaryKey?: string | null;
  estMinutes?: number;
}): Promise<{ id: string }> {
  return api('/admin/content/lessons', { method: 'POST', body: input });
}
export async function updateLesson(
  id: string,
  input: Partial<{
    titleKey: string;
    summaryKey: string | null;
    estMinutes: number;
    sortOrder: number;
  }>,
): Promise<void> {
  await api(`/admin/content/lessons/${id}`, { method: 'PATCH', body: input });
}
export async function deleteLesson(id: string): Promise<void> {
  await api(`/admin/content/lessons/${id}`, { method: 'DELETE' });
}
export async function reorderLessons(parentId: string, ids: string[]): Promise<void> {
  await api('/admin/content/lessons/reorder', { method: 'POST', body: { parentId, ids } });
}

// ─── Activities ─────────────────────────────────────────────────────────────

export const ACTIVITY_TYPES = [
  'intro_video',
  'letter_card',
  'vocabulary_card',
  'tap_match',
  'drag_sort',
  'multiple_choice',
  'fill_blank',
  'listen_choose',
  'record_repeat',
  'letter_trace',
  'read_aloud_passage',
  'story_scene',
  'quiz_summary',
  'printable_homework',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export async function createActivity(input: {
  lessonId: string;
  type: string;
  data: unknown;
  scoringRules?: { passScore?: number; xp?: number; graded?: boolean };
}): Promise<{ id: string }> {
  return api('/admin/content/activities', { method: 'POST', body: input });
}
export async function updateActivity(
  id: string,
  input: {
    data?: unknown;
    scoringRules?: { passScore?: number; xp?: number; graded?: boolean };
    sortOrder?: number;
  },
): Promise<void> {
  await api(`/admin/content/activities/${id}`, { method: 'PATCH', body: input });
}
export async function deleteActivity(id: string): Promise<void> {
  await api(`/admin/content/activities/${id}`, { method: 'DELETE' });
}
export async function reorderActivities(parentId: string, ids: string[]): Promise<void> {
  await api('/admin/content/activities/reorder', { method: 'POST', body: { parentId, ids } });
}

// ─── Publish state ──────────────────────────────────────────────────────────

export async function setPublishState(
  entityType: 'unit' | 'lesson' | 'activity',
  id: string,
  status: ContentStatus,
): Promise<{ ok: boolean; status: ContentStatus; version?: number }> {
  return api(`/admin/content/${entityType}/${id}/publish-state`, {
    method: 'POST',
    body: { status },
  });
}

// ─── Version history + restore + preview ────────────────────────────────────

export interface ContentVersion {
  version: number;
  createdAt: string;
  createdBy: string | null;
}

export async function listLessonVersions(
  lessonId: string,
  signal?: AbortSignal,
): Promise<ContentVersion[]> {
  const { versions } = await api<{ versions: ContentVersion[] }>(
    `/admin/content/lessons/${lessonId}/versions`,
    { signal },
  );
  return versions;
}

export async function restoreLessonVersion(
  lessonId: string,
  version: number,
): Promise<{ ok: boolean; version: number }> {
  return api(`/admin/content/lessons/${lessonId}/restore/${version}`, { method: 'POST' });
}

// A learner-shaped view of the lesson (answer keys stripped) for preview.
export interface PreviewActivity {
  id: string;
  type: string;
  sortOrder: number;
  payload: unknown;
  scoringRules: { passScore: number; xp: number; graded: boolean };
}
export interface LessonPreview {
  lesson: {
    id: string;
    titleKey: string;
    summaryKey: string | null;
    estMinutes: number;
    subjectSlug: string;
    grade: string;
  };
  activities: PreviewActivity[];
}

export async function getLessonPreview(
  lessonId: string,
  signal?: AbortSignal,
): Promise<LessonPreview> {
  return api(`/admin/content/lessons/${lessonId}/preview`, { signal });
}
